import React, { Component, lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import { AuthProvider } from './context/AuthContext';
import { useFavoriteSports } from './hooks/useFavoriteSports';
import Footer from './components/Footer';
import NotificationPrompt from './components/NotificationPrompt';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';

// ── Eagerly loaded (always needed on first paint) ─────────────────────────────
import Home from './pages/Home';
import SportsOnboarding from './pages/SportsOnboarding';

// ── Lazily loaded (split into separate chunks) ────────────────────────────────
const LoginPage           = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage        = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const LogWorkout     = lazy(() => import('./pages/LogWorkout'));
const History        = lazy(() => import('./pages/History'));
const Walking        = lazy(() => import('./pages/activities/Walking'));
const Gym            = lazy(() => import('./pages/activities/Gym'));
const Badminton      = lazy(() => import('./pages/activities/Badminton'));
const Skating        = lazy(() => import('./pages/activities/Skating'));
const GymSchedule    = lazy(() => import('./pages/GymSchedule'));
const StepTracker    = lazy(() => import('./pages/StepTracker'));
const SportsHub      = lazy(() => import('./pages/SportsHub'));
const WaterTracker   = lazy(() => import('./pages/WaterTracker'));
const WeightTracker  = lazy(() => import('./pages/WeightTracker'));
const CalorieTracker = lazy(() => import('./pages/CalorieTracker'));
const WorkoutTimer   = lazy(() => import('./pages/WorkoutTimer'));
const Analytics      = lazy(() => import('./pages/Analytics'));
const MealPlanner    = lazy(() => import('./pages/MealPlanner'));
const BMICalculator  = lazy(() => import('./pages/BMICalculator'));
const SleepTracker   = lazy(() => import('./pages/SleepTracker'));
const Profile        = lazy(() => import('./pages/Profile'));
const PrivacyPolicy  = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const ContactUs      = lazy(() => import('./pages/ContactUs'));
const DownloadApp    = lazy(() => import('./pages/DownloadApp'));
const RateApp        = lazy(() => import('./pages/RateApp'));

// ── Shared fallback spinner ───────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600" />
    </div>
  );
}

// ── Error boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <button onClick={() => this.setState({ hasError: false })}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── My-sports edit wrapper ────────────────────────────────────────────────────
function MySportsEditPage() {
  const navigate = useNavigate();
  const { favorites, toggle, complete, skip } = useFavoriteSports();
  return (
    <SportsOnboarding
      favorites={favorites}
      onToggle={toggle}
      onComplete={() => { complete(); navigate('/'); }}
      onSkip={() => { skip(); navigate('/'); }}
    />
  );
}

// ── Main shell ────────────────────────────────────────────────────────────────
function AppInner() {
  const { done, checked, favorites, toggle, complete, skip } = useFavoriteSports();
  const [swRegistration, setSwRegistration] = useState(null);

  useEffect(() => {
    const handler = (e) => setSwRegistration(e.detail);
    window.addEventListener('sw-update', handler);
    return () => window.removeEventListener('sw-update', handler);
  }, []);

  if (!checked) return null;

  if (!done) {
    return (
      <SportsOnboarding
        favorites={favorites}
        onToggle={toggle}
        onComplete={complete}
        onSkip={skip}
      />
    );
  }

  return (
    <div className="app-shell">
      <Navigation />
      <NotificationPrompt />
      <PWAInstallPrompt />
      <PWAUpdatePrompt registration={swRegistration} onDismiss={() => setSwRegistration(null)} />
      <main className="app-main">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"                      element={<Home />} />
            <Route path="/login"                 element={<LoginPage />} />
            <Route path="/register"              element={<RegisterPage />} />
            <Route path="/forgot-password"       element={<ForgotPasswordPage />} />
            <Route path="/log"                   element={<LogWorkout />} />
            <Route path="/history"               element={<History />} />
            <Route path="/activities/walking"    element={<Walking />} />
            <Route path="/activities/gym"        element={<Gym />} />
            <Route path="/activities/badminton"  element={<Badminton />} />
            <Route path="/activities/skating"    element={<Skating />} />
            <Route path="/schedule"              element={<GymSchedule />} />
            <Route path="/steps"                 element={<StepTracker />} />
            <Route path="/sports"                element={<SportsHub />} />
            <Route path="/water"                 element={<WaterTracker />} />
            <Route path="/weight"                element={<WeightTracker />} />
            <Route path="/calories"              element={<CalorieTracker />} />
            <Route path="/timer"                 element={<WorkoutTimer />} />
            <Route path="/my-sports"             element={<MySportsEditPage />} />
            <Route path="/analytics"             element={<Analytics />} />
            <Route path="/meals"                 element={<MealPlanner />} />
            <Route path="/bmi"                   element={<BMICalculator />} />
            <Route path="/sleep"                 element={<SleepTracker />} />
            <Route path="/profile"               element={<Profile />} />
            <Route path="*"                      element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/privacy"  element={<PrivacyPolicy />} />
              <Route path="/terms"    element={<TermsAndConditions />} />
              <Route path="/contact"  element={<ContactUs />} />
              <Route path="/download" element={<DownloadApp />} />
              <Route path="/rate"     element={<RateApp />} />
              <Route path="*"         element={<AppInner />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
